// @file Vue composable for PostHog
import environment from '@@/bits/environment'
import { isAppUsing } from '@@/bits/flip'
import type { PostHogConfig } from 'posthog-js'
import { sampleByEvent } from 'posthog-js/lib/src/customizations'

const POSTHOG_TOKEN =
  environment === 'production'
    ? 'phc_Qq7IdaGdpavwycUT45k9GGcwMUjV1XIRx3zGRe1Nxrc'
    : 'phc_EPCt6qTBrd32zI0egjOAA8SoeriXYmG5y369SNYZNOh'

// Decided once per module load (i.e. once per page load), not re-rolled on every usePostHog() call —
// usePostHog() can be invoked multiple times per page (identify, onSubmit, onDismiss, etc.), and
// re-rolling each time would inflate the effective sampling rate well above 0.1%.
const IS_IN_SESSION_RECORDING_SAMPLE = Math.random() < 0.001

// Module-level cache: every caller shares this exact same promise, so posthog.init() runs exactly once
// per page load instead of once per caller. Calling posthog.init() more than once reconfigures the same
// singleton and can reset in-flight survey/feature-flag state.
let posthogPromise: ReturnType<typeof initPostHog> | undefined
function usePostHog(): ReturnType<typeof initPostHog> {
  posthogPromise ??= initPostHog().catch((e) => {
    // Clear the cache on failure so a later call can retry -- otherwise a single transient failure
    // (e.g. a chunk-load blip on the dynamic import) would permanently cache a rejected promise, making
    // PostHog unavailable for the rest of the page load even though the underlying cause was transient.
    posthogPromise = undefined
    throw e
  })
  return posthogPromise
}

const posthogConfig: Partial<PostHogConfig> = {
  api_host: 'https://echo.padlet.com',
  ui_host: 'https://us.posthog.com',

  // Disable surveys if the flip is not enabled
  disable_surveys: !isAppUsing('posthogNpsSurvey'),

  // Disable automatic analytics events
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_dead_clicks: false,

  persistence: 'localStorage',
  person_profiles: 'identified_only',
  // Disable session recording by default so we can control sampling rate programmatically
  disable_session_recording: true,
  // Sample 0.1% of web vitals events if flip is enabled
  before_send: sampleByEvent(['$web_vitals'], isAppUsing('posthogSessionRecording') ? 0.1 : 0),
}

async function initPostHog() {
  const { posthog } = await import('posthog-js')
  posthog.init(POSTHOG_TOKEN, posthogConfig)

  if (isAppUsing('posthogSessionRecording') && IS_IN_SESSION_RECORDING_SAMPLE) {
    posthog.startSessionRecording()
  }

  return { posthog }
}

export { usePostHog }
